import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Trash2 } from 'lucide-react';
import { deleteUserApi, listUsersApi, updateUserApi } from '@/features/users/api';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { useToast } from '@/components/ui/Toast';
import { useAuth } from '@/features/auth/AuthContext';
import { getApiErrorMessage } from '@/lib/api-client';
import type { Role } from '@/features/auth/types';

const ROLE_TONES: Record<Role, 'brand' | 'info' | 'neutral'> = {
  admin: 'brand',
  editor: 'info',
  viewer: 'neutral',
};

export default function UsersPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const { user: me } = useAuth();
  const query = useQuery({ queryKey: ['users'], queryFn: () => listUsersApi() });

  const updateMut = useMutation({
    mutationFn: ({ id, input }: { id: string; input: Parameters<typeof updateUserApi>[1] }) =>
      updateUserApi(id, input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['users'] }),
  });
  const removeMut = useMutation({
    mutationFn: deleteUserApi,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['users'] }),
  });

  const handleDelete = async (id: string, email: string) => {
    if (!confirm(`Desativar usuário ${email}?`)) return;
    try {
      await removeMut.mutateAsync(id);
      toast.success('Usuário desativado.');
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Falha ao desativar.'));
    }
  };

  const isAdmin = me?.role === 'admin';

  return (
    <div className="space-y-6 max-w-5xl">
      <header>
        <h1 className="text-2xl font-semibold">Usuários</h1>
        <p className="text-sm text-text-muted">
          Membros com acesso à plataforma. Novos usuários são criados automaticamente como{' '}
          <strong>Editor</strong> na primeira vez que fazem login com Google @starbridge.com.br.
        </p>
      </header>

      <Card>
        <CardHeader>
          <h2 className="text-base font-semibold">Lista</h2>
        </CardHeader>
        <CardBody>
          {query.isLoading ? (
            <div className="flex justify-center text-text-muted">
              <Spinner />
            </div>
          ) : !query.data || query.data.items.length === 0 ? (
            <p className="text-text-muted text-sm">Nenhum usuário.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-text-muted text-left">
                <tr>
                  <th className="py-2">Email</th>
                  <th>Nome</th>
                  <th>Role</th>
                  <th>Ativo</th>
                  <th className="text-right">Ações</th>
                </tr>
              </thead>
              <tbody>
                {query.data.items.map((u) => (
                  <tr key={u.id} className="border-t border-border">
                    <td className="py-2 font-mono">{u.email}</td>
                    <td>{u.displayName || '—'}</td>
                    <td>
                      {isAdmin ? (
                        <select
                          value={u.role}
                          disabled={u.id === me?.id}
                          onChange={(e) =>
                            updateMut.mutate({
                              id: u.id,
                              input: { role: e.target.value as Role },
                            })
                          }
                          className="rounded-md border border-border bg-surface-base px-2 py-1 text-xs"
                        >
                          <option value="viewer">viewer</option>
                          <option value="editor">editor</option>
                          <option value="admin">admin</option>
                        </select>
                      ) : (
                        <Badge tone={ROLE_TONES[u.role]} className="uppercase">
                          {u.role}
                        </Badge>
                      )}
                    </td>
                    <td>
                      {u.isActive ? (
                        <Badge tone="brand">ativo</Badge>
                      ) : (
                        <Badge tone="neutral">inativo</Badge>
                      )}
                    </td>
                    <td className="text-right">
                      {isAdmin && u.id !== me?.id && (
                        <button
                          onClick={() => handleDelete(u.id, u.email)}
                          className="text-red-400 hover:text-red-300"
                          title="Desativar"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
